import React, { useEffect, useState } from 'react';
import './ResetPassword.scss';
import { Grid } from '@mui/material';
import { DefaultButton, PrimaryButton } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import authAxios from "../../services/Api";
import { useDispatch } from 'react-redux';
import { addLog } from '../../store/auth-store';
import { useForm } from "react-hook-form";
import TokenService from "../../services/token.service";
import { showSuccessToast } from "../../shared/CustomToast/customtoast";
import { handleError } from "../../shared/ErrorHandler/ErrorHandler";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SVGIcon from '../../components/DynamicIcon';

const SPECIAL_CHAR_REGEX = /[!@#$%^&*(),.?":{}|<>]/;

const ResetPassword = ({ open, onClose, onSaved }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const { register, handleSubmit, watch, formState: { errors }, reset } = useForm();
  const password = watch("newPassword", "");
  const decryptData = (encryptedData) => TokenService.decryptData(encryptedData);

  useEffect(() => {
    reset({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  }, [reset]);

  const logOut = () => {
    authAxios.post('api/v1/user/logout')
      .then(response => {
        if (response.status === 200) {
          TokenService.removeUser(); // clears cookies, sessionStorage, and localStorage
          dispatch(addLog(false));
          navigateTo('/login');
        }
      }).catch(error => {
        handleError(error, 5000);
      });
  };

  const handleCancel = () => {
    const userType = decryptData(Cookies.get("user_type"));
    (userType === 'Admin') ? navigateTo('/dashboard') : navigateTo('/dashboard')
  };
  const handleCancelClick = () => {
    reset({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    onClose();
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    const formData = {
      current_password: data.currentPassword,
      new_password: data.newPassword,
      confirm_password: data.confirmPassword,
    };

    authAxios.put('api/v1/user/resetMyPassword', formData)
      .then(response => {
        if (response.status === 200) {
          showSuccessToast("Password changed successfully!");
          setIsLoading(false);
          // Just hide the dialog — logOut() below handles navigation, so we
          // skip onClose()'s cancel-to-dashboard side effects here.
          (onSaved ?? onClose)();
          logOut();
        }
      }).catch(error => {
        handleError(error, 5000);
        setIsLoading(false);
      });
  };
  const getPasswordStrength = () => {
    let strength = 0;

    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (SPECIAL_CHAR_REGEX.test(password)) strength++;

    return strength; // 0 to 4
  };

  return (

    <>
      <Dialog
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.7) !important',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            width: "521px",
            padding: '21px',
          },
        }}
      >
        <div className='content-overview-container homeScreen'>
          <div>
            <IconButton
              aria-label="Close"
              onClick={handleCancelClick}
              sx={{ position: 'absolute', top: '10px', right: '10px' }}
            >
              <CloseIcon />
            </IconButton>
            <div className='reset-password-header-div'>
              <SVGIcon name="resetPassword" width={40} height={40} />
              <span className="reset-password-header">
                <span className="header_colour">Change Password</span>
                <span className='reset-password-subname'>Strengthen your security with a new password</span>
              </span>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className='reset-form-div'>
              <Grid container>
                <Grid item xs={12}>
                  <div className="container_row-new">
                    <label htmlFor="currentPassword" className="farm_form_label-new">
                      Current Password<span className="farm_required_symbol">*</span>
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      className={`container_row_reset ${errors.currentPassword ? "input-error" : ""
                        }`}
                      {...register("currentPassword", { required: "Current Password is required" })}
                    />
                    <span className="form_error_message">{errors.currentPassword && <div>{errors.currentPassword.message}</div>}</span>
                  </div>
                  <div className="container_row-new">
                    <label htmlFor="newPassword" className="farm_form_label-new">
                      New Password<span className="farm_required_symbol">*</span>
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      className={`container_row_reset ${errors.newPassword ? "input-error" : ""
                        }`}
                      {...register("newPassword", {
                        required: "New Password is required",
                        minLength: {
                          value: 8,
                          message: "Minimum 8 characters required"
                        },
                        validate: {
                          hasUpperCase: value =>
                            /[A-Z]/.test(value) || "At least one uppercase letter required",
                          hasNumber: value =>
                            /[0-9]/.test(value) || "At least one number required",
                          hasSpecialChar: value =>
                            SPECIAL_CHAR_REGEX.test(value) || "At least one special character required"
                        }
                      })}
                    />
                    <span className="form_error_message">{errors.newPassword && <div>{errors.newPassword.message}</div>}</span>
                  </div>
                  <div className="container_row-new">
                    <label htmlFor="confirmPassword" className="farm_form_label-new">
                      Confirm Password<span className="farm_required_symbol">*</span>
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      className={`container_row_reset ${errors.confirmPassword ? "input-error" : ""
                        }`}
                      {...register("confirmPassword", {
                        required: "Confirm Password is required",
                        validate: value =>
                          value === password || "Passwords do not match"
                      })}
                    />
                    <span className="form_error_message">{errors.confirmPassword && <div>{errors.confirmPassword.message}</div>}</span>
                  </div>
                  <div className="password-strength-container">
                    <div className="strength-cylinders">
                      <div className={`cylinder ${getPasswordStrength() >= 1 ? "active weak" : ""}`}></div>
                      <div className={`cylinder ${getPasswordStrength() >= 2 ? "active medium" : ""}`}></div>
                      <div className={`cylinder ${getPasswordStrength() >= 3 ? "active strong" : ""}`}></div>
                      <div className={`cylinder ${getPasswordStrength() >= 4 ? "active strong" : ""}`}></div>
                    </div>

                    <div className="password-rules">
                      <p className={/[A-Z]/.test(password) ? "valid" : "invalid"}>
                        ✔ At least 1 uppercase
                      </p>
                      <p className={/[0-9]/.test(password) ? "valid" : "invalid"}>
                        ✔ At least 1 number
                      </p>
                      <p className={SPECIAL_CHAR_REGEX.test(password) ? "valid" : "invalid"}>
                        ✔ At least 1 special character
                      </p>
                      <p className={password.length >= 8 ? "valid" : "invalid"}>
                        ✔ At least 8 characters
                      </p>
                    </div>
                  </div>
                  <div className='form_normal_button_row'>
                    <div className='form_button_element button_padding'>
                      <DefaultButton
                        text="Cancel"
                        className="form_popup_btn_cancel-reset reset-cancel-btn"
                        onClick={handleCancelClick} />
                    </div>
                    <div className='form_btn_margin_right'>
                      <PrimaryButton className="form_popup_btn_margin-reset" disabled={isLoading} type="submit">
                        {isLoading && (
                          <i className="fa fa-refresh fa-spin" style={{ marginRight: "5px" }} />
                        )}
                        {isLoading ? 'Saving...' : 'Save'}
                      </PrimaryButton>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </form>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ResetPassword;